import React, { useContext, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { Channel } from 'talkplus-sdk/dist/model/types';
import Portal from '@common/portal';
import ChatHeader from '@components/chat/common/chatHeader';
import ChatListItemMenu from '@components/chat/common/chatListItemMenu';
import { ChatContext, ChatContextType } from '@components/chat/ChatContext';
import { useChatEnterLog, useChatToken } from '@repository/chat/useChat';
import { useUserInfo } from '@repository/auth/useUserInfo';
import { getAccessToken } from '@repository/AccessToken';
import ChatList from '@components/chat/chatList';
import ChatSearch from '@components/chat/chatSearch';
import ChatDetail from '@components/chat/chatDetail';
import { ChatListenerDataType } from '@components/chat/hooks/useChatListener';
import ChatModals from '@components/chat/modals';
import { ChatModalEnum } from '@components/chat/hooks/useChatModals';
import { ChatMenu } from '@domain/constant/ChatMenuType';
import usePosition from '@utils/hooks/usePosition';
import useGlobalBtnDisplay from '@utils/hooks/useGlobalBtnDisplay';
import useClickChatOutSide from '@utils/hooks/useClickChatOutside';
import classnames from 'classnames/bind';
import styles from './index.module.scss';

const cx = classnames.bind(styles);

interface ChatProps {
  chatSNBVisible: boolean;
  setChatSNBVisible: (chatSNBVisible: boolean) => void;
}

const Chat = ({ chatSNBVisible, setChatSNBVisible }: ChatProps) => {
  const {
    chatMenu,
    userData: chatUserData,
    setChatMenu,
    loginChatWithToken,
    loginChatAnonymous,
    logoutChat,
    fetchMyChannels,
    fetchGetPublicChannels,
    fetchMessages,
    setMessagesData,
    channelsData,
    updateNotification,
    myChannelsData,
    setMyChannelsData,
    joinChannel,
    setChannelInfo,
    setChannel,
    onChat,
    openModal,
    closeModal,
  } = useContext<ChatContextType>(ChatContext);

  const router = useRouter();
  const chatRef = useRef<HTMLDivElement | null>(null);

  const { data: userData } = useUserInfo();
  const { data: tokenData } = useChatToken(!!userData?.id);
  const { mutate: fetchChatEnterLog } = useChatEnterLog();

  const isLogin = !!getAccessToken();

  // 채팅 목록 ... 클릭 시 출력되는 메뉴 위치 조절
  const { position: menuPosition, handlePosition: handleMenuPosition } = usePosition();

  // 채팅방 ESC 키 이벤트
  useClickChatOutSide(chatRef, () => setChatSNBVisible(false));

  // 깃플챗 등 전역 버튼 숨김 처리
  useGlobalBtnDisplay({ state: chatSNBVisible, deps: [chatSNBVisible, chatMenu] });

  /** 잡다 로그인 한 경우
   * 1. 백엔드에서 token을 받아와서 chat에 로그인 시킨다.
   * 2. talkpuls에서 채팅방을 조회한다.
   * */
  useEffect(() => {
    if (!isLogin) return;
    if (!tokenData) return;
    if (!userData) return;

    (async () => {
      await loginChatWithToken({ userId: userData.id, loginToken: tokenData.token });
      await fetchMyChannels();
      await fetchGetPublicChannels();
    })();
  }, [userData, tokenData]);

  /** 로그인 하지 않은 경우
   * 1. talkplus에 익명으로 로그인 시킨다.
   * 2. talkpuls에서 채팅방을 조회한다.
   * 3. 익명 로그아웃
   * */
  useEffect(() => {
    if (isLogin) return;

    (async () => {
      await loginChatAnonymous();
      setMyChannelsData({ channels: [], hasNext: false });
      await fetchGetPublicChannels();
      await logoutChat();
    })();
  }, []);

  /** 쿼리로 channelId가 넘어온 경우
   * 1. 채널 정보를 조회한다.
   * 2. 채널 정보를 저장한다.
   * 3. 채널에 참여한다.
   * */
  useEffect(() => {
    if (!router.query.channelId) return;
    if (!chatUserData) return;
    if (!myChannelsData) return;
    if (!channelsData) return;
    closeModal();
    setChatSNBVisible(true);

    (async () => {
      const channel = channelsData?.channels?.find((channel) => channel.id === router.query.channelId);
      if (!channel) return;
      setChannelInfo(channel);
      await handleChannelClick(channel);

      // 채널 정보를 url에서 제거
      const { query } = router;
      delete query.channelId;

      router.replace(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { shallow: true },
      );
    })();
  }, [router.query.channelId, chatUserData, myChannelsData, channelsData]);

  const handleCloseChatSNB = () => setChatSNBVisible(false);

  const updateChannelsMemberCount = (channel: Channel) => {
    const isJoin = !!myChannelsData?.channels?.find((c) => c.id === channel.id);
    const findChannel = channelsData?.channels?.find((c) => c.id === channel.id);
    if (!findChannel) return;
    findChannel.memberCount = isJoin ? channel?.memberCount || 0 : (channel?.memberCount || 0) + 1;
    setChannelInfo({ ...findChannel });
  };

  const handleChannelClick = async (channel: Channel) => {
    // 로그인 안한 유저는 로그인 모달 출력, 로그인 후 채팅방 입장을 위해 채널 정보 저장
    if (!isLogin) {
      setChannelInfo(channel);
      openModal(ChatModalEnum.LOGIN);
      return;
    }

    // 채팅에 참여하지 않은 경우 참여
    const isJoin = !!myChannelsData?.channels?.find((myChannel) => myChannel.id === channel.id);
    if (!isJoin) await joinChannel(channel);

    // 채팅방 메시지 조회
    await fetchMessages(channel);

    // 채팅방 정보 갱신
    setChannelInfo({ ...channel, memberCount: isJoin ? channel.memberCount : channel.memberCount + 1 });
    setChannel(channel);
    updateChannelsMemberCount(channel);

    // 채팅방 알림 갱신
    updateNotification(channel.id);

    // 채팅방 상세로 이동
    setChatMenu(ChatMenu.DETAIL);

    // 채팅 리스너 연결
    onChat({
      channel,
      handleChatListenerMessage: (data) => handleListenerMessage(data),
      handleChatListenerMemberAdded: (data) => setChannelInfo(data.channel),
      handleChatListenerMemberLeft: (data) => setChannelInfo(data.channel),
      handleChatListenerMessageDeleted: async (data) => await fetchMessages(channel),
    });

    // 내 채널 갱신
    fetchMyChannels();

    // 백엔드 채팅방 입장 로그 전송
    fetchChatEnterLog({ channelId: channel.id });
  };

  const handleListenerMessage = (data: ChatListenerDataType) => {
    setMessagesData((prev) => {
      if (!prev) return null;
      if (prev.messages.find((message) => message.id === data.message.id)) return prev;
      return {
        ...prev,
        messages: [...prev.messages, data.message],
      };
    });
  };

  const getContentsEl = () => {
    switch (chatMenu) {
      case ChatMenu.LIST:
        return <ChatList handleChannelClick={handleChannelClick} handleMenuPosition={handleMenuPosition} />;
      case ChatMenu.SEARCH:
        return <ChatSearch handleChannelClick={handleChannelClick} />;
      case ChatMenu.DETAIL:
        return <ChatDetail />;
      default:
        return null;
    }
  };

  return (
    <>
      <Portal selector="#chat">
        <div id={'chatArea'} ref={chatRef} className={cx('chatArea', { visible: chatSNBVisible })}>
          <ChatHeader handleCloseChatSNB={handleCloseChatSNB} />
          {getContentsEl()}
        </div>
      </Portal>
      <ChatModals />
      <ChatListItemMenu position={menuPosition} />
    </>
  );
};

export default Chat;
